import type { FormEvent, ReactNode } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { LocationFields } from "../../components/LocationFields";
import { NEW_VENUE, venueLine } from "../../core/primitives/hosted-event";
import { formatLocationAddress } from "../../core/primitives/location-form";
import { EventEditActions, EventEditState } from "./event-edit-model";

const labelCls = "mb-1.5 block text-xs font-medium text-white";

function Field({ id, label, error, children }: { id: string; label: ReactNode; error?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

export function EventEditPage() {
  const state = useSubtree(EventEditState);
  const actions = useSubtree(EventEditActions);
  const observer = useObserver();
  const creating = observer.watch(state.creating);
  const status = observer.watch(state.status);
  const draft = observer.watch(state.draft);
  const errors = observer.watch(state.errors);
  const venueDraft = observer.watch(state.venueDraft);
  const venueErrors = observer.watch(state.venueErrors);
  const regions = observer.watch(state.regions);
  const countries = observer.watch(state.countries);
  const countriesStatus = observer.watch(state.countriesStatus);
  const locations = observer.watch(state.locations);
  const place = observer.watch(state.place);
  const zones = observer.watch(state.zones);
  const form = observer.watch(state.form);
  const cancelled = observer.watch(state.cancelledAt) !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void actions.save();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4">
      <header>
        <h1 className="text-xl font-semibold">
          {creating ? "Host a show" : cancelled ? "A cancelled show" : "Edit the show"}
        </h1>
        {creating && (
          <p className="text-sm opacity-60">
            Its tables come next. It stays a draft, which nobody else sees, until you publish it.
          </p>
        )}
      </header>

      {status.kind === "loading" && <p className="opacity-60">Loading the show…</p>}
      {status.kind === "error" && <p className="note note-danger">{status.message}</p>}

      {status.kind === "loaded" && (
        <form onSubmit={submit} className="panel flex flex-col gap-4 p-4">
          <fieldset disabled={cancelled} className="flex flex-col gap-4">
            <Field id="show-title" label="Show name" error={errors.title}>
              <input
                id="show-title"
                value={draft.title}
                onChange={(e) => actions.setField("title", e.target.value)}
                className="field"
                placeholder="e.g. Fall Card Show"
                maxLength={200}
              />
            </Field>

            {creating ? (
              <Field id="show-venue" label="Where" error={errors.venue}>
                <select
                  id="show-venue"
                  value={draft.venue}
                  onChange={(e) => actions.setField("venue", e.target.value)}
                  className="field"
                >
                  <option value="">Choose the venue</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                      {formatLocationAddress(location) ? ` · ${formatLocationAddress(location)}` : ""}
                    </option>
                  ))}
                  <option value={NEW_VENUE}>Somewhere else…</option>
                </select>
              </Field>
            ) : (
              place && (
                <div>
                  <p className={labelCls}>Where</p>
                  <p className="text-sm">{venueLine(place)}</p>
                  <p className="mt-1 text-xs opacity-60">The venue stays as it is once the show exists.</p>
                </div>
              )
            )}

            {creating && draft.venue === NEW_VENUE && (
              <div className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
                <LocationFields
                  kind="venue"
                  draft={venueDraft}
                  errors={venueErrors}
                  countries={countries}
                  countriesStatus={countriesStatus}
                  regions={regions}
                  onChange={actions.setVenueField}
                  idPrefix="venue"
                />
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="show-start-date" label="Starts" error={errors.startDate ?? errors.startTime}>
                <div className="flex gap-2">
                  <input
                    id="show-start-date"
                    type="date"
                    value={draft.startDate}
                    onChange={(e) => actions.setField("startDate", e.target.value)}
                    className="field min-w-0 flex-1"
                  />
                  <input
                    type="time"
                    value={draft.startTime}
                    onChange={(e) => actions.setField("startTime", e.target.value)}
                    className="field w-28"
                    aria-label="Opening time"
                  />
                </div>
              </Field>
              <Field id="show-end-date" label="Ends" error={errors.endDate ?? errors.endTime}>
                <div className="flex gap-2">
                  <input
                    id="show-end-date"
                    type="date"
                    value={draft.endDate}
                    onChange={(e) => actions.setField("endDate", e.target.value)}
                    className="field min-w-0 flex-1"
                  />
                  <input
                    type="time"
                    value={draft.endTime}
                    onChange={(e) => actions.setField("endTime", e.target.value)}
                    className="field w-28"
                    aria-label="Closing time"
                  />
                </div>
              </Field>
            </div>

            <Field id="show-zone" label="Time zone of the venue" error={errors.timezone}>
              <select
                id="show-zone"
                value={draft.timezone}
                onChange={(e) => actions.setField("timezone", e.target.value)}
                className="field"
              >
                {zones.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              id="show-link"
              label={
                <>
                  Link <span className="font-normal text-faint">Optional</span>
                </>
              }
              error={errors.shareUrl}
            >
              <input
                id="show-link"
                type="url"
                value={draft.shareUrl}
                onChange={(e) => actions.setField("shareUrl", e.target.value)}
                className="field"
                placeholder="https://"
              />
            </Field>

            <Field
              id="show-description"
              label={
                <>
                  About the show <span className="font-normal text-faint">Optional</span>
                </>
              }
            >
              <textarea
                id="show-description"
                value={draft.description}
                onChange={(e) => actions.setField("description", e.target.value)}
                className="field min-h-24"
                maxLength={5000}
              />
            </Field>
          </fieldset>

          {form.error && <p className="note note-danger">{form.error}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={actions.cancel}>
              {cancelled ? "Back" : "Cancel"}
            </button>
            {!cancelled && (
              <button type="submit" className="btn-primary" disabled={form.busy}>
                {form.busy ? "Saving…" : creating ? "Save and add tables" : "Save"}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
